import { RenewalJob, RenewalPreview } from '../api/models';
import { TagTone } from './ui';

export const RENEWAL_CONFIRM_WORD = 'confirm';

/** Terms an operator may pick. A longer term multiplies a mistake. */
export const RENEWAL_YEAR_OPTIONS = [1, 2, 3];

// A domain never contains a space, so a line holding several of them is kept
// whole and reported as the one bad line it is rather than split into
// fragments nobody typed.
export function parseDomainList(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
}

export function duplicateDomains(domains: string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const domain of domains) {
    if (seen.has(domain)) duplicates.add(domain);
    seen.add(domain);
  }
  return Array.from(duplicates);
}

export function isConfirmed(text: string): boolean {
  return text.trim().toLowerCase() === RENEWAL_CONFIRM_WORD;
}

// A halted run has stopped for good even though it left domains unrenewed, so
// the client stops polling it like any other finished run.
export function isTerminalJobStatus(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'halted';
}

export function jobStatusTone(status: string): TagTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'accent';
    case 'halted':
      return 'warn';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function itemStatusTone(status: string): TagTone {
  switch (status) {
    case 'renewed':
      return 'success';
    case 'renewing':
      return 'accent';
    case 'skipped':
      return 'neutral';
    case 'unknown':
      return 'warn';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function itemStatusLabel(status: string): string {
  switch (status) {
    case 'unknown':
      return 'Needs Checking';
    case 'renewing':
      return 'Renewing';
    case 'renewed':
      return 'Renewed';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Waiting';
    default:
      return status;
  }
}

export function formatCents(cents: number | undefined, currency: string): string {
  if (cents === undefined || cents === null) return '—';
  const amount = (cents / 100).toFixed(2);
  return currency ? `${amount} ${currency}` : amount;
}

// Namecheap adds the term to the expiry the domain already has rather than to
// today, so the new date follows from the current one. It is an estimate: a
// registry can refuse a term that would take a domain past its maximum.
// A reactivation is not a term added to a long-past date, so it has no estimate
// worth showing.
export function estimatedNewExpiry(
  expiresAt: Date | null | undefined,
  years: number,
  action?: string,
): Date | null {
  if (action === 'reactivate') return null;
  if (!expiresAt) return null;
  const estimated = new Date(expiresAt.getTime());
  estimated.setFullYear(estimated.getFullYear() + years);
  return estimated;
}

export function shortfallCents(preview: RenewalPreview): number {
  const shortfall = preview.estimatedTotalCents - preview.availableBalanceCents;
  return shortfall > 0 ? shortfall : 0;
}

// The single predicate the start button binds to. A batch can only be started
// when the server said it could and the operator typed the word.
export function canStartRenewal(preview: RenewalPreview | null, confirmText: string): boolean {
  if (!preview) return false;
  if (!preview.canProceed) return false;
  if (preview.eligibleCount === 0) return false;
  return isConfirmed(confirmText);
}

export function jobProgress(job: RenewalJob): { done: number; total: number; pct: number } {
  const done = job.succeeded + job.failed;
  const total = job.totalDomains;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

export function isReactivation(item: { action?: string }): boolean {
  return item.action === 'reactivate';
}

export function actionLabel(item: { action?: string }): string | null {
  return isReactivation(item) ? 'Will reactivate' : null;
}

export function actionTone(item: { action?: string }): TagTone {
  return isReactivation(item) ? 'warn' : 'neutral';
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

// What the batch is actually made of, so one confirmation does not hide an
// expensive restore among ordinary renewals.
export function compositionSentence(preview: RenewalPreview): string {
  const parts = [plural(preview.renewCount ?? 0, 'renewal')];
  if ((preview.reactivateCount ?? 0) > 0) {
    let clause = plural(preview.reactivateCount, 'reactivation');
    if ((preview.premiumReactivateCount ?? 0) > 0) {
      clause += `, of which ${preview.premiumReactivateCount} premium`;
    }
    parts.push(clause);
  }
  return parts.join(' and ');
}

export function excludedPremiumSentence(preview: RenewalPreview): string | null {
  const excluded = preview.excludedPremiumReactivations ?? [];
  if (excluded.length === 0) return null;
  return `${plural(excluded.length, 'expired premium name')} left out: the registrar only reactivates a premium name for one year. Run them as their own one-year batch.`;
}

// A run that had to use the other command says so, because what it charged
// follows from which command it used.
export function actionUsedNote(item: { action?: string; actionUsed?: string }): string | null {
  if (!item.actionUsed || item.actionUsed === item.action) return null;
  return item.actionUsed === 'reactivate'
    ? 'Reactivated instead: the registrar considered this expired.'
    : 'Renewed instead: the registrar considered this still renewable.';
}

// How a batch's prices were arrived at, which differs per domain: the registrar
// quotes a premium name individually and validates that quote when the renewal
// is submitted, while an ordinary name is priced from the account's list for its
// TLD and so can still be moved by a promotion.
export function pricingBasisSentence(preview: RenewalPreview): string {
  const items = (preview.items ?? []).filter((item) => item.eligible);
  const anyPremium = items.some((item) => item.isPremium);
  const anyListed = items.some((item) => !item.isPremium);

  if (anyPremium && !anyListed) {
    return "Premium names are quoted by the registrar for that exact name, and it refuses the renewal if the price has moved since. Each domain records what it was charged as the run works through.";
  }
  if (anyListed && !anyPremium) {
    return "Prices come from the account's list for each TLD, so a promotion can still change what a renewal actually costs. Each domain records what it was charged as the run works through.";
  }
  return "Premium names are quoted by the registrar for that exact name; the rest come from the account's price list, where a promotion can still change what a renewal costs. Each domain records what it was charged as the run works through.";
}
